import React, { useState } from 'react';
import { UploadCloud, DownloadCloud, Zap, QrCode, Clipboard } from 'lucide-react';

const FileTransferPage = () => {
  // State to manage the active view: 'landing', 'send', or 'receive'
  const [view, setView] = useState('landing'); 
  
  // State for the generated code (Sender) or entered code (Receiver)
  const [transferCode, setTransferCode] = useState('');
  
  // State for file selection (Sender)
  const [selectedFile, setSelectedFile] = useState(null);
  
  // State for receiver's input
  const [receiverCodeInput, setReceiverCodeInput] = useState('');

  // --- Functions ---
  
  const generateCode = () => {
    // Simple 6-digit alphanumeric code generation
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setTransferCode(code);
    setView('send');
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(transferCode);
    alert(`Code copied: ${transferCode}`);
  };

  const handleReceiveFile = (e) => {
    e.preventDefault();
    // In a real app, this is where you'd validate the code and start the download
    alert(`Attempting to receive file using code: ${receiverCodeInput}`);
    // Simulated successful transition:
    setView('landing'); 
    setReceiverCodeInput('');
  };

  // --- Sub-Components ---

  // 1. Landing View
  const LandingView = () => (
    <>
      <h1 className="text-5xl font-extrabold text-gray-900 mb-6">
        Fast, Secure File Transfer
      </h1>
      <p className="text-xl text-gray-600 mb-12">
        Send and receive large files peer-to-peer, instantly.
      </p>

      <div className="flex space-x-8">
        
        {/* Send File Option */}
        <div className="w-64 p-8 bg-white rounded-xl shadow-2xl border border-gray-100 hover:shadow-indigo-300/50 transition duration-300">
          <UploadCloud size={48} className="text-indigo-600 mb-4 mx-auto" />
          <h2 className="text-2xl font-bold text-center mb-4">Send File</h2>
          <button
            onClick={generateCode}
            className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition"
          >
            Start Sending
          </button>
        </div>

        {/* Receive File Option */}
        <div className="w-64 p-8 bg-white rounded-xl shadow-2xl border border-gray-100 hover:shadow-green-300/50 transition duration-300">
          <DownloadCloud size={48} className="text-green-600 mb-4 mx-auto" />
          <h2 className="text-2xl font-bold text-center mb-4">Receive File</h2>
          <button
            onClick={() => setView('require_code')}
            className="w-full py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
          >
            Start Receiving
          </button>
        </div>
      </div>
    </>
  );

  // 2. Send File View
  const SendView = () => (
    <div className="w-full max-w-lg bg-white p-10 rounded-xl shadow-2xl border border-indigo-200 text-center">
      <Zap size={48} className="text-indigo-600 mb-6 mx-auto" />
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Share this Code</h2>
      
      {/* Generated Code Display */}
      <div className="flex items-center justify-center space-x-3 mb-8">
        <p className="text-5xl font-mono tracking-widest bg-indigo-50 text-indigo-800 p-4 rounded-lg border border-indigo-300 shadow-inner">
          {transferCode}
        </p>
        <button 
          onClick={handleCopyCode}
          className="p-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          title="Copy Code"
        >
          <Clipboard size={24} />
        </button>
      </div>
      
      {/* File Upload Area */}
      {selectedFile ? (
        <div className="p-4 border-2 border-dashed border-green-400 bg-green-50 rounded-lg mb-6">
          <p className="font-semibold text-green-700">{selectedFile.name}</p>
          <p className="text-sm text-green-600">Ready to transfer.</p>
        </div>
      ) : (
        <label className="block w-full cursor-pointer bg-gray-100 hover:bg-gray-200 p-6 rounded-lg border-2 border-dashed border-gray-300 mb-6 transition">
          <UploadCloud size={32} className="text-gray-500 mx-auto mb-2" />
          <span className="text-gray-700 font-medium">Click to select file</span>
          <input type="file" className="hidden" onChange={handleFileChange} />
        </label>
      )}

      <button
        onClick={() => { /* Start actual transfer logic here */ alert("Starting File Transfer!"); }}
        disabled={!selectedFile}
        className={`w-full py-3 text-lg font-semibold rounded-lg transition ${
          selectedFile
            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
            : 'bg-gray-300 text-gray-600 cursor-not-allowed'
        }`}
      >
        Transfer File
      </button>

      <button 
        onClick={() => setView('landing')}
        className="mt-4 text-sm text-indigo-500 hover:text-indigo-600"
      >
        Cancel
      </button>
    </div>
  );

  // 3. Require Code View (Receiver)
  const RequireCodeView = () => (
    <div className="w-full max-w-sm bg-white p-8 rounded-xl shadow-2xl border border-green-200 text-center">
      <QrCode size={48} className="text-green-600 mb-6 mx-auto" />
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Enter Transfer Code</h2>
      
      <form onSubmit={handleReceiveFile} className="space-y-6">
        <input
          type="text"
          maxLength="6"
          value={receiverCodeInput}
          onChange={(e) => setReceiverCodeInput(e.target.value.toUpperCase())}
          placeholder="e.g. A1B2C3"
          required
          className="w-full text-center text-3xl font-mono tracking-widest p-3 border-2 border-green-300 rounded-lg focus:outline-none focus:border-green-500"
        />
        
        <button
          type="submit"
          disabled={receiverCodeInput.length !== 6}
          className={`w-full py-3 text-lg font-semibold rounded-lg transition ${
            receiverCodeInput.length === 6
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
        >
          Download File
        </button>
      </form>

      <button 
        onClick={() => setView('landing')}
        className="mt-4 text-sm text-green-500 hover:text-green-600"
      >
        Cancel
      </button>
    </div>
  );

  // --- Main Render ---
  return (
    <div className="flex flex-col min-h-screen bg-gray-50 font-sans">
      
      {/* Header (Minimal) */}
      <header className="py-4 px-8 border-b border-gray-200 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-3xl font-bold text-indigo-600 flex items-center">
            <Zap size={28} className="mr-2" /> SnapShare
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow flex items-center justify-center p-8 text-center">
        
        {view === 'landing' && <LandingView />}
        {view === 'send' && <SendView />}
        {view === 'require_code' && <RequireCodeView />}
        
      </main>

      {/* Footer (Minimal) */}
      <footer className="py-4 px-8 bg-white border-t border-gray-200 text-center text-sm text-gray-500">
        <p>&copy; {new Date().getFullYear()} SnapShare. Designed for fast peer-to-peer transfers.</p>
      </footer>
    </div>
  );
};

export default FileTransferPage;